import { useCallback, useMemo } from 'react';
import { productsStationList } from '@/api/generated/products-stations/products-stations';
import type { StationListItem } from '@/api/generated/schemas';
import { Badge } from '@/shared/ui';
import { col, defineListPage, filter, type ListPageConfig } from '@/shared/ui/list-page';
import { NavListPage } from '@/shared/navigation/react/NavListPage';
import { useNavigator } from '@/shared/navigation/react/useNavigator';
import { stationDetailAgenda } from '../detail/stationDetailAgenda';
import { stationListAgenda } from './stationListAgenda';
import { STATION_LIST_KEY } from '../internal/stationsQueryKeys';

/**
 * Šablona ListPage stanic — deklaruje sloupce, filtry a akci „Nová stanice"; kompletní chování
 * seznamu (vč. tlačítka na konci toolbaru) dodává engine `@/shared/ui/list-page`. Klíče filtrů a
 * řazení odpovídají BE `StationListSpec`.
 */
function buildStationListConfig(
  openStation: (id: number | undefined) => void
): ListPageConfig<StationListItem> {
  return {
    key: STATION_LIST_KEY,
    i18n: { namespace: 'products' },
    api: { list: (request, signal) => productsStationList(request, signal) },
    rowKey: (item) => item.id,
    defaultSort: { column: 'name', desc: false },
    fulltext: { placeholder: (t) => t('stationList.search') },
    onOpen: (item) => openStation(item.id),
    create: { label: (t) => t('stationList.new'), onClick: () => openStation(undefined) },
    columns: (t) => [
      col.mono<StationListItem>('code', {
        label: t('stationList.cols.code'),
        w: 110,
        frozen: true,
      }),
      col.strong<StationListItem>('name', {
        label: t('stationList.cols.name'),
        w: 220,
        frozen: true,
      }),
      col.text<StationListItem>('typeName', { label: t('stationList.cols.type'), w: 130 }),
      col.text<StationListItem>('destinationName', {
        label: t('stationList.cols.destination'),
        w: 160,
      }),
      col.num<StationListItem>('placesCount', {
        label: t('stationList.cols.places'),
        w: 64,
        sortKey: 'places',
      }),
      col.custom<StationListItem>({
        key: 'isDomestic',
        label: t('stationList.cols.kind'),
        w: 104,
        sortable: false,
        cell: (item) => (
          <Badge tone={item.isDomestic ? 'success' : 'info'}>
            {item.isDomestic ? t('stationList.kind.in') : t('stationList.kind.out')}
          </Badge>
        ),
      }),
      col.custom<StationListItem>({
        key: 'gps',
        label: t('stationList.cols.gps'),
        w: 140,
        sortable: false,
        cell: (item) =>
          item.latitude != null && item.longitude != null ? (
            <span className="text-phx-xs font-mono">
              {item.latitude.toFixed(4)}, {item.longitude.toFixed(4)}
            </span>
          ) : (
            <span className="text-phx-text-faint">—</span>
          ),
      }),
    ],
    filterGroups: (t) => [{ id: 'basic', label: t('stationList.groups.basic') }],
    filters: (t) => [
      filter.text('name', { label: t('stationList.filters.name'), group: 'basic', freq: true }),
      filter.text('code', { label: t('stationList.filters.code'), group: 'basic', freq: true }),
      filter.text('destinationName', {
        label: t('stationList.filters.destination'),
        group: 'basic',
      }),
    ],
  };
}

/**
 * Listpage stanic. Otevření řádku i akce „Nová stanice" (tlačítko na konci toolbaru) míří na
 * `stationDetailAgenda` (modální vrstva).
 */
export function StationListPage({ layerId }: { layerId: string }) {
  const navigator = useNavigator();

  const openStation = useCallback(
    (id: number | undefined) => {
      void navigator.openModal(stationDetailAgenda, { stationId: id });
    },
    [navigator]
  );

  const config = useMemo(() => buildStationListConfig(openStation), [openStation]);

  return <NavListPage agenda={stationListAgenda} layerId={layerId} config={config} />;
}

/**
 * Výběrový (picker) režim ListPage stanic — pro ⋯ akci selektoru (native náhrada legacy Magic listu
 * přes registr ObjectCategory). V picker režimu se řádky vybírají, `onOpen`/`create` se nevolají,
 * proto no-op opener.
 */
export const StationListPicker = defineListPage(buildStationListConfig(() => {})).Picker;
